// Per-cylinder waveform-ensemble small-multiples view. One SVG panel
// per cylinder showing every cycle's slot waveform faintly overlaid
// with a bold mean curve. Used by admin to visually confirm that
// cyls have distinguishable time-varying signatures (and to spot the
// outlier when one is misfiring or knocking).
//
// Lazy-loaded — clicking "Load ensembles" hits
// /api/admin/recordings/:id/cylinder-ensemble which decodes the FLAC
// and slices it. ~1–3 s per recording depending on duration.

import { ReactNode, useMemo, useState } from 'react';
import { api } from './api';

// Formatting helpers for the inline statistical readouts. Mirror the
// server-side `fmtP` / `pStars` so display is consistent with what
// the analysis code emits.
function fmtP(p: number | undefined): string {
  if (p == null || !Number.isFinite(p) || p < 0) return '—';
  if (p < 1e-6) return '<1e-6';
  if (p < 0.001) return '<0.001';
  return p.toFixed(3);
}
function pStars(p: number | undefined): string {
  if (p == null || !Number.isFinite(p)) return '';
  if (p < 0.001) return '***';
  if (p < 0.01)  return '**';
  if (p < 0.05)  return '*';
  return '';
}
/** Color for an effect-size or significance signal: significant with
 *  meaningful Cohen's d → accent; significant but small d → muted;
 *  non-significant → faded. */
function pColor(p: number | undefined): string {
  if (p == null) return 'var(--muted)';
  if (p < 0.05) return 'var(--accent)';
  return 'var(--muted)';
}

interface EnsembleResponse {
  recording_id: number;
  vehicle_id: number | null;
  num_cylinders: number;
  rotation: number;
  assignments: Record<number, number> | null;
  cycles_total: number;
  cycles_returned: number;
  slot_samples: number;
  display_samples: number;
  duration_per_slot_ms: number;
  /** Fraction of the displayed waveform that is padding before/after
   *  the slot. 0.10 means the slot occupies the middle ~83 % of the
   *  display, with 10 % onset context on each side. */
  display_pad_frac: number;
  spectrum_bins: number;
  spectrum_freq_max_hz: number;
  cylinders: Array<{
    template_slot: number;
    recording_slot: number;
    letter: string;
    assigned_cyl: number | null;
    cycles: number[][];
    mean: number[];
    spectrum_db: number[];
    stats: {
      n_cycles: number;
      snr_db: number;
      cv_pct: number;
      mean_peak: number;
      mean_peak_ci95: number;
      vs_cohort_t: number;
      vs_cohort_p: number;
      vs_cohort_d: number;
      peak_freq_hz: number;
      peak_db: number;
      peak_snr_db: number;
      peak_p_value: number;
    };
  }>;
}

interface Props {
  recordingId: number;
}

interface PerCylIcaResponse {
  recording_id: number;
  num_cylinders: number;
  num_components: number;
  rotation: number;
  assignments: Record<number, number> | null;
  duration_per_slot_ms: number;
  display_samples: number;
  cycles_used: number;
  cylinders: Array<{
    template_slot: number;
    recording_slot: number;
    letter: string;
    assigned_cyl: number | null;
    components: number[][];
    compvars: number[];
    variance_fractions: number[];
    error?: string;
  }>;
}

interface IcaMappingResponse {
  recording_id: number;
  num_cylinders: number;
  rotation: number;
  assignments: Record<number, number> | null;
  ica_aligned: boolean;
  alignment_warning?: string;
  error?: string;
  recomputed?: boolean;
  cycle_samples: number;
  display_samples: number;
  duration_per_cycle_ms: number;
  cylinders: Array<{
    template_slot: number;
    recording_slot: number;
    letter: string;
    assigned_cyl: number | null;
    mapped_components: Array<{
      component_index: number;
      peak_position_samples: number;
      peak_value: number;
      rms: number;
      variance_fraction: number;
      mapping_strength: number;
      waveform: number[];
    }>;
  }>;
  unmapped: Array<{
    component_index: number;
    peak_position_samples: number;
    peak_value: number;
    rms: number;
    variance_fraction: number;
    reason: string;
    waveform: number[];
  }>;
}

export function CylinderEnsembleView({ recordingId }: Props) {
  const [data, setData] = useState<EnsembleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllCycles, setShowAllCycles] = useState(true);
  const [overlayMode, setOverlayMode] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<EnsembleResponse>(
        `/api/admin/recordings/${recordingId}/cylinder-ensemble`,
      );
      setData(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // Global Y-scale across all panels so the visual comparison is
  // honest. Use the max abs value across mean + sample cycles, with a
  // small headroom.
  const yScale = useMemo(() => {
    if (!data) return 1;
    let peak = 0;
    for (const c of data.cylinders) {
      for (const v of c.mean) if (Math.abs(v) > peak) peak = Math.abs(v);
      for (const cyc of c.cycles) {
        for (const v of cyc) if (Math.abs(v) > peak) peak = Math.abs(v);
      }
    }
    return peak > 0 ? peak * 1.1 : 1;
  }, [data]);

  return (
    <div className="panel">
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>
          Per-cylinder waveform ensembles
          {data && (
            <span className="muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
              {' '}({data.num_cylinders} cyls × {data.cycles_returned} cycles
              {data.assignments ? ', calibrated' : ', uncalibrated'})
            </span>
          )}
        </h3>
        <div className="row" style={{ gap: '0.4rem', alignItems: 'center' }}>
          {data && (
            <>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
              }}>
                <input
                  type="checkbox" checked={showAllCycles}
                  onChange={(e) => setShowAllCycles(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                show all cycles
              </label>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
              }}>
                <input
                  type="checkbox" checked={overlayMode}
                  onChange={(e) => setOverlayMode(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                overlay all cyls
              </label>
            </>
          )}
          <button
            onClick={load}
            disabled={loading}
            className={data ? 'secondary' : undefined}
          >
            {loading ? 'Loading…' : data ? 'Reload' : 'Load ensembles'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: 6 }}>{error}</div>
      )}

      {!data && !loading && !error && (
        <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
          Click <strong>Load ensembles</strong> to decode the FLAC and slice
          out per-cylinder, per-cycle waveforms. Each panel below will show
          one engine cylinder, with every recorded cycle overlaid faintly
          and the across-cycle mean drawn in bold. Useful for visually
          confirming that cylinders have distinguishable time signatures —
          and for spotting the misfiring one (its mean will look unlike its
          neighbors').
        </div>
      )}

      {data && (
        <>
          <DescriptionBox
            title="What this view shows: time-domain per-cyl ensemble"
            body={
              <>
                <p>
                  Each panel = one engine cylinder. The faded thin lines
                  are every recorded engine cycle's slot waveform overlaid;
                  the bold accent line is the across-cycles mean. The
                  <strong> shaded vertical region</strong> marks the slot
                  itself (the firing window, {data.duration_per_slot_ms.toFixed(2)} ms);
                  the gray strips on either side show
                  {' '}{((data.display_pad_frac ?? 0) * 100).toFixed(0)}% of context
                  before slot start (so you can see the firing onset rising
                  out of the previous cyl's tail) and {((data.display_pad_frac ?? 0) * 100).toFixed(0)}%
                  after (so you see the firing decay before the next cyl
                  takes over). Y-axis is amplitude shared across panels.
                </p>
                <p>
                  <strong>Expect</strong>: each cyl's mean shows a sharp
                  transient near slot start (the firing event) decaying
                  through the slot. Healthy engine — overall shapes
                  similar, peak amplitudes within ±20%, scatter (faded
                  fan) ≈ 5–10% of mean. Misfiring cyl — its mean is
                  noticeably weaker / missing the firing event.
                </p>
                <p>
                  <strong>Synthetic V6 example</strong>: cyl 5 has 1200 Hz
                  fundamental with 7 odd harmonics → expect a dense ringy
                  burst; cyl 6 has 1500 Hz × 3 harmonics → expect a smoother
                  near-square shape. All six means should look distinctly
                  different. The pre-slot context should look quiet (the
                  prior cyl's signal has decayed) and the post-slot
                  context should show the start of the next cyl's onset.
                </p>
              </>
            }
          />
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
            Slot duration: {data.duration_per_slot_ms.toFixed(2)} ms.
            Rotation r={data.rotation}. Y-axis shared across panels for
            honest comparison; range ±{yScale.toFixed(3)}.
          </div>
          {overlayMode ? (
            <OverlayPanel data={data} yScale={yScale} />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
              gap: '0.5rem', marginTop: 8,
            }}>
              {data.cylinders.map((c) => (
                <SinglePanel
                  key={c.template_slot}
                  cyl={c}
                  yScale={yScale}
                  showAllCycles={showAllCycles}
                  durationMs={data.duration_per_slot_ms}
                  padFrac={data.display_pad_frac ?? 0}
                />
              ))}
            </div>
          )}

          <SpectrumSection data={data} />
          <IcaMappingSection recordingId={recordingId} />
          <PerCylIcaSection recordingId={recordingId} />
        </>
      )}
    </div>
  );
}

// ---- Single-cyl panel ------------------------------------------------

function SinglePanel({
  cyl, yScale, showAllCycles, durationMs, padFrac,
}: {
  cyl: EnsembleResponse['cylinders'][number];
  yScale: number;
  showAllCycles: boolean;
  /** Slot duration only (firing window). Padding adds 2*padFrac to the
   *  displayed window total. */
  durationMs: number;
  /** 0..0.5 — fraction of the displayed waveform that's padding on
   *  each side (0 = no padding). The slot occupies the middle. */
  padFrac: number;
}) {
  const W = 220, H = 120;
  const margin = { l: 26, r: 6, t: 6, b: 14 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;
  const N = cyl.mean.length;
  const xOf = (i: number) => margin.l + (i / (N - 1)) * innerW;
  const yOf = (v: number) => margin.t + ((1 - (v / yScale + 1) / 2)) * innerH;
  // Slot region in pixel space: padFrac in from each side of the
  // inner width. With padFrac=0.10, the displayed window is
  // pre + slot + post = 1 unit, and slot = 0.10..0.90 fraction.
  const totalWindowFrac = 1 + 2 * padFrac;
  const slotStartX = margin.l + (padFrac / totalWindowFrac) * innerW;
  const slotEndX   = margin.l + ((padFrac + 1) / totalWindowFrac) * innerW;

  const meanPath = cyl.mean
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`)
    .join(' ');

  const cyclePaths = showAllCycles
    ? cyl.cycles.map((cyc) =>
        cyc.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(' '),
      )
    : [];

  const title = cyl.assigned_cyl != null
    ? `Cyl ${cyl.assigned_cyl}`
    : `P-${cyl.letter}`;
  const sub = cyl.assigned_cyl != null
    ? `P-${cyl.letter} · slot #${cyl.recording_slot + 1}`
    : `slot #${cyl.recording_slot + 1}`;

  return (
    <div style={{
      background: 'var(--panel-2)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '0.4rem 0.5rem',
    }}>
      <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '0.9rem' }}>{title}</strong>
        <span className="muted" style={{ fontSize: '0.7rem' }}>{sub}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height: H, display: 'block', marginTop: 2 }}>
        {/* Slot-region highlight: the firing window is the middle of
            the displayed waveform; the regions on either side are
            pre-firing context (left) and post-firing decay (right). */}
        {padFrac > 0 && (
          <rect
            x={slotStartX} y={margin.t}
            width={Math.max(1, slotEndX - slotStartX)} height={innerH}
            fill="var(--accent)" opacity={0.06}
          />
        )}
        {padFrac > 0 && (
          <>
            <line x1={slotStartX} x2={slotStartX} y1={margin.t} y2={margin.t + innerH}
                  stroke="var(--accent)" strokeWidth={0.6} strokeDasharray="3 2" opacity={0.5} />
            <line x1={slotEndX} x2={slotEndX} y1={margin.t} y2={margin.t + innerH}
                  stroke="var(--accent)" strokeWidth={0.6} strokeDasharray="3 2" opacity={0.5} />
          </>
        )}
        {/* zero baseline */}
        <line
          x1={margin.l} x2={W - margin.r}
          y1={yOf(0)} y2={yOf(0)}
          stroke="var(--border)" strokeWidth={0.5} strokeDasharray="2 3"
        />
        {/* y-axis ticks */}
        <text x={margin.l - 3} y={yOf(yScale)} fontSize="8" fill="var(--muted)" textAnchor="end" dominantBaseline="middle">
          {yScale.toFixed(2)}
        </text>
        <text x={margin.l - 3} y={yOf(0)} fontSize="8" fill="var(--muted)" textAnchor="end" dominantBaseline="middle">
          0
        </text>
        <text x={margin.l - 3} y={yOf(-yScale)} fontSize="8" fill="var(--muted)" textAnchor="end" dominantBaseline="middle">
          {(-yScale).toFixed(2)}
        </text>
        {/* faded cycle lines */}
        {cyclePaths.map((d, i) => (
          <path key={i} d={d} stroke="var(--accent)" strokeWidth={0.5}
                fill="none" opacity={0.18} />
        ))}
        {/* mean line */}
        <path d={meanPath} stroke="var(--accent)" strokeWidth={1.6} fill="none" />
        {/* x-axis labels: slot edges + total window */}
        {padFrac > 0 ? (
          <>
            <text x={slotStartX} y={H - 3} fontSize="8" fill="var(--accent)" textAnchor="middle">
              slot
            </text>
            <text x={slotEndX} y={H - 3} fontSize="8" fill="var(--accent)" textAnchor="middle">
              {durationMs.toFixed(1)}ms
            </text>
          </>
        ) : (
          <>
            <text x={margin.l} y={H - 3} fontSize="8" fill="var(--muted)">0</text>
            <text x={W - margin.r} y={H - 3} fontSize="8" fill="var(--muted)" textAnchor="end">
              {durationMs.toFixed(1)} ms
            </text>
          </>
        )}
      </svg>
      {cyl.stats && <PanelStatsLine stats={cyl.stats} />}
    </div>
  );
}

/** Inline stats readout under the SinglePanel SVG. SNR + CV come from
 *  per-cycle peak amplitudes within the slot; the t-test/d compare
 *  this cyl to the pooled rest-of-cyls. */
function PanelStatsLine({ stats }: { stats: EnsembleResponse['cylinders'][number]['stats'] }) {
  return (
    <div className="muted" style={{ fontSize: '0.7rem', marginTop: 2, lineHeight: 1.35 }}>
      <div>
        <strong>SNR</strong> {Number.isFinite(stats.snr_db) ? stats.snr_db.toFixed(1) : '—'} dB
        {' · '}
        <strong>CV</strong> {stats.cv_pct.toFixed(1)}%
        {' · '}
        n={stats.n_cycles}
      </div>
      <div>
        vs cohort:{' '}
        <span style={{ color: pColor(stats.vs_cohort_p) }}>
          d={stats.vs_cohort_d.toFixed(2)}, p={fmtP(stats.vs_cohort_p)}{pStars(stats.vs_cohort_p)}
        </span>
      </div>
    </div>
  );
}

// ---- Overlay-all-cyls panel ------------------------------------------

const CYL_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#a855f7', '#06b6d4', '#84cc16', '#ec4899',
  '#fb923c', '#14b8a6', '#a3e635', '#f472b6',
];

function OverlayPanel({
  data, yScale,
}: { data: EnsembleResponse; yScale: number }) {
  const W = 720, H = 240;
  const margin = { l: 38, r: 130, t: 10, b: 22 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;
  const N = data.cylinders[0]?.mean.length ?? 0;
  if (N < 2) return <div className="muted">no data</div>;
  const xOf = (i: number) => margin.l + (i / (N - 1)) * innerW;
  const yOf = (v: number) => margin.t + ((1 - (v / yScale + 1) / 2)) * innerH;
  const padFrac = data.display_pad_frac ?? 0;
  const totalWindowFrac = 1 + 2 * padFrac;
  const slotStartX = margin.l + (padFrac / totalWindowFrac) * innerW;
  const slotEndX   = margin.l + ((padFrac + 1) / totalWindowFrac) * innerW;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block',
                  background: 'var(--panel-2)', borderRadius: 4, marginTop: 8 }}>
      {padFrac > 0 && (
        <>
          <rect x={slotStartX} y={margin.t}
                width={Math.max(1, slotEndX - slotStartX)} height={innerH}
                fill="var(--accent)" opacity={0.05} />
          <line x1={slotStartX} x2={slotStartX} y1={margin.t} y2={margin.t + innerH}
                stroke="var(--accent)" strokeWidth={0.7} strokeDasharray="3 2" opacity={0.4} />
          <line x1={slotEndX} x2={slotEndX} y1={margin.t} y2={margin.t + innerH}
                stroke="var(--accent)" strokeWidth={0.7} strokeDasharray="3 2" opacity={0.4} />
        </>
      )}
      <line x1={margin.l} x2={W - margin.r} y1={yOf(0)} y2={yOf(0)}
            stroke="var(--border)" strokeWidth={0.5} strokeDasharray="2 3" />
      <text x={margin.l - 4} y={yOf(yScale)} fontSize="9" fill="var(--muted)" textAnchor="end" dominantBaseline="middle">
        {yScale.toFixed(2)}
      </text>
      <text x={margin.l - 4} y={yOf(0)} fontSize="9" fill="var(--muted)" textAnchor="end" dominantBaseline="middle">
        0
      </text>
      <text x={margin.l - 4} y={yOf(-yScale)} fontSize="9" fill="var(--muted)" textAnchor="end" dominantBaseline="middle">
        {(-yScale).toFixed(2)}
      </text>
      {data.cylinders.map((c, idx) => {
        const color = CYL_COLORS[idx % CYL_COLORS.length];
        const path = c.mean
          .map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`)
          .join(' ');
        const label = c.assigned_cyl != null ? `Cyl ${c.assigned_cyl} (P-${c.letter})` : `P-${c.letter}`;
        const yLegend = margin.t + 12 + idx * 14;
        return (
          <g key={c.template_slot}>
            <path d={path} stroke={color} strokeWidth={1.4} fill="none" />
            <line x1={W - margin.r + 8} x2={W - margin.r + 22}
                  y1={yLegend} y2={yLegend}
                  stroke={color} strokeWidth={2} />
            <text x={W - margin.r + 26} y={yLegend + 3} fontSize="10" fill="var(--fg)">
              {label}
            </text>
          </g>
        );
      })}
      {padFrac > 0 ? (
        <>
          <text x={slotStartX} y={H - 6} fontSize="9" fill="var(--accent)" textAnchor="middle">
            slot start
          </text>
          <text x={slotEndX} y={H - 6} fontSize="9" fill="var(--accent)" textAnchor="middle">
            slot end ({data.duration_per_slot_ms.toFixed(1)} ms)
          </text>
        </>
      ) : (
        <>
          <text x={margin.l} y={H - 6} fontSize="9" fill="var(--muted)">0</text>
          <text x={W - margin.r} y={H - 6} fontSize="9" fill="var(--muted)" textAnchor="end">
            {data.duration_per_slot_ms.toFixed(1)} ms
          </text>
        </>
      )}
    </svg>
  );
}

// ---- Per-cylinder magnitude-spectrum section ------------------------
//
// Cycle-averaged |FFT| in dB per cyl, rendered as small-multiples
// directly below the time-domain ensembles. Magnitude averaging
// (not waveform averaging) keeps resonance features stable across
// cycles even when phase shifts a little.

const SPECTRUM_DEFAULT_MAX_HZ = 10000;

function SpectrumSection({ data }: { data: EnsembleResponse }) {
  const [maxHz, setMaxHz] = useState(SPECTRUM_DEFAULT_MAX_HZ);
  const [logFreq, setLogFreq] = useState(false);
  const [overlay, setOverlay] = useState(false);

  // Shared y-scale across panels — find dB range over all cyls.
  const { yMin, yMax } = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    const stop = Math.min(
      data.spectrum_bins,
      Math.ceil((maxHz / data.spectrum_freq_max_hz) * (data.spectrum_bins - 1)) + 1,
    );
    for (const c of data.cylinders) {
      // Skip the DC bin (always loud, drowns the y-scale).
      for (let k = 1; k < stop; k += 1) {
        const v = c.spectrum_db[k];
        if (v > -160 && v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    if (!isFinite(lo) || !isFinite(hi)) return { yMin: -60, yMax: 0 };
    return { yMin: Math.max(lo, hi - 80), yMax: hi + 3 };
  }, [data, maxHz]);

  return (
    <div style={{ marginTop: 14 }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>Per-cylinder magnitude spectrum</h4>
        <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
          }}>
            max freq
            <select
              value={maxHz}
              onChange={(e) => setMaxHz(Number(e.target.value))}
              style={{ width: 'auto', fontSize: '0.78rem', padding: '2px 4px' }}
            >
              <option value={2000}>2 kHz</option>
              <option value={5000}>5 kHz</option>
              <option value={10000}>10 kHz</option>
              <option value={20000}>20 kHz</option>
              <option value={data.spectrum_freq_max_hz}>Nyquist ({(data.spectrum_freq_max_hz/1000).toFixed(0)} kHz)</option>
            </select>
          </label>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
          }}>
            <input type="checkbox" checked={logFreq}
                   onChange={(e) => setLogFreq(e.target.checked)}
                   style={{ width: 'auto' }} />
            log freq
          </label>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
          }}>
            <input type="checkbox" checked={overlay}
                   onChange={(e) => setOverlay(e.target.checked)}
                   style={{ width: 'auto' }} />
            overlay all cyls
          </label>
        </div>
      </div>
      <DescriptionBox
        title="What this view shows: per-cyl FFT magnitude spectrum"
        body={
          <>
            <p>
              For each cyl, take that cyl's slot waveform from every cycle,
              FFT each one independently, then average the magnitudes
              across cycles. Plotted in dB. Mean-of-magnitudes (not
              |FFT(mean)|) so phase shifts between cycles don't cancel
              real energy.
            </p>
            <p>
              <strong>Expect</strong>: engine firing-rate harmonics
              clustered &lt; 1 kHz (multiples of {data.spectrum_freq_max_hz > 0 ? '~90 Hz' : 'firing rate'}),
              mechanical resonance band 1–5 kHz (intake/exhaust/valve
              activity), knock band 5–8 kHz. A knocking cyl sits
              noticeably above its neighbors in the 5–8 kHz band; a
              flow-restricted cyl shows a depressed mid-band.
            </p>
            <p>
              <strong>Synthetic V6 example</strong>: every cyl shows a
              distinct fundamental peak at its synthesized frequency —
              cyl 1 = 600 Hz (peaks at 600, 1800, 3000),
              cyl 2 = 700 Hz (peaks at 700, 2100, 3500, 4900),
              cyl 5 = 1200 Hz (peaks at 1200, 3600, 6000, 8400, ...),
              cyl 6 = 1500 Hz (peaks at 1500, 4500, 7500). Switch the
              max-freq selector to 10 kHz to see them all.
            </p>
          </>
        }
      />
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
        Cycle-averaged |FFT| (Hann-windowed). Y range {yMin.toFixed(0)} to {yMax.toFixed(0)} dB.
      </div>
      {overlay ? (
        <SpectrumOverlay data={data} maxHz={maxHz} logFreq={logFreq} yMin={yMin} yMax={yMax} />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
          gap: '0.5rem', marginTop: 8,
        }}>
          {data.cylinders.map((c) => (
            <SpectrumPanel
              key={c.template_slot}
              cyl={c}
              freqMaxHz={data.spectrum_freq_max_hz}
              maxHz={maxHz}
              logFreq={logFreq}
              yMin={yMin}
              yMax={yMax}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface SpectrumPanelProps {
  cyl: EnsembleResponse['cylinders'][number];
  freqMaxHz: number;
  maxHz: number;
  logFreq: boolean;
  yMin: number;
  yMax: number;
}

function freqAxisX(freq: number, maxHz: number, logFreq: boolean): number {
  if (logFreq) {
    // Log scale from 50 Hz to maxHz; clamp inputs to avoid log(0).
    const lo = 50;
    const hi = maxHz;
    const f = Math.max(lo, Math.min(hi, freq));
    return (Math.log10(f) - Math.log10(lo)) / (Math.log10(hi) - Math.log10(lo));
  }
  return freq / maxHz;
}

function SpectrumPanel({
  cyl, freqMaxHz, maxHz, logFreq, yMin, yMax,
}: SpectrumPanelProps) {
  const W = 220, H = 120;
  const margin = { l: 30, r: 6, t: 6, b: 16 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;
  const N = cyl.spectrum_db.length;

  const points: string[] = [];
  for (let k = 1; k < N; k += 1) {     // skip DC
    const f = (k / (N - 1)) * freqMaxHz;
    if (f > maxHz) break;
    const v = cyl.spectrum_db[k];
    const xn = freqAxisX(f, maxHz, logFreq);
    const x = margin.l + xn * innerW;
    const ynRaw = (v - yMin) / (yMax - yMin);
    const yn = Math.max(0, Math.min(1, ynRaw));
    const y = margin.t + (1 - yn) * innerH;
    points.push(`${points.length === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`);
  }

  const title = cyl.assigned_cyl != null ? `Cyl ${cyl.assigned_cyl}` : `P-${cyl.letter}`;
  const sub = cyl.assigned_cyl != null
    ? `P-${cyl.letter} · slot #${cyl.recording_slot + 1}`
    : `slot #${cyl.recording_slot + 1}`;

  // Tick labels for x-axis: 0, midpoint, max (linear) or 100/1k/10k etc (log)
  const xTicks = logFreq
    ? [100, 1000, maxHz].filter((f) => f >= 50 && f <= maxHz)
    : [0, maxHz / 2, maxHz];

  return (
    <div style={{
      background: 'var(--panel-2)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '0.4rem 0.5rem',
    }}>
      <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '0.9rem' }}>{title}</strong>
        <span className="muted" style={{ fontSize: '0.7rem' }}>{sub}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height: H, display: 'block', marginTop: 2 }}>
        {/* y-axis labels */}
        <text x={margin.l - 3} y={margin.t + 4} fontSize="8" fill="var(--muted)" textAnchor="end">
          {yMax.toFixed(0)}
        </text>
        <text x={margin.l - 3} y={margin.t + innerH + 3} fontSize="8" fill="var(--muted)" textAnchor="end">
          {yMin.toFixed(0)}
        </text>
        {/* spectrum line */}
        <path d={points.join(' ')} stroke="var(--accent)" strokeWidth={1.4} fill="none" />
        {/* x-axis ticks */}
        {xTicks.map((f) => {
          const xn = freqAxisX(f, maxHz, logFreq);
          const x = margin.l + xn * innerW;
          const label = f >= 1000 ? `${(f / 1000).toFixed(0)}k` : `${f.toFixed(0)}`;
          return (
            <text key={f} x={x} y={H - 4} fontSize="8" fill="var(--muted)"
                  textAnchor={f === xTicks[xTicks.length - 1] ? 'end' : (f === xTicks[0] ? 'start' : 'middle')}>
              {label}
            </text>
          );
        })}
      </svg>
      {cyl.stats && (
        <div className="muted" style={{ fontSize: '0.7rem', marginTop: 2, lineHeight: 1.35 }}>
          peak {cyl.stats.peak_freq_hz.toFixed(0)} Hz @ {cyl.stats.peak_db.toFixed(1)} dB
          {' · '}
          <span style={{ color: pColor(cyl.stats.peak_p_value) }}>
            SNR {cyl.stats.peak_snr_db.toFixed(1)} dB above median, p={fmtP(cyl.stats.peak_p_value)}{pStars(cyl.stats.peak_p_value)}
          </span>
        </div>
      )}
    </div>
  );
}

function SpectrumOverlay({
  data, maxHz, logFreq, yMin, yMax,
}: {
  data: EnsembleResponse; maxHz: number; logFreq: boolean; yMin: number; yMax: number;
}) {
  const W = 720, H = 240;
  const margin = { l: 38, r: 130, t: 10, b: 22 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;
  const N = data.cylinders[0]?.spectrum_db.length ?? 0;
  if (N < 2) return null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block',
                  background: 'var(--panel-2)', borderRadius: 4, marginTop: 8 }}>
      <text x={margin.l - 4} y={margin.t + 4} fontSize="9" fill="var(--muted)" textAnchor="end">
        {yMax.toFixed(0)} dB
      </text>
      <text x={margin.l - 4} y={margin.t + innerH + 3} fontSize="9" fill="var(--muted)" textAnchor="end">
        {yMin.toFixed(0)} dB
      </text>
      {data.cylinders.map((c, idx) => {
        const color = CYL_COLORS[idx % CYL_COLORS.length];
        const points: string[] = [];
        for (let k = 1; k < N; k += 1) {
          const f = (k / (N - 1)) * data.spectrum_freq_max_hz;
          if (f > maxHz) break;
          const xn = freqAxisX(f, maxHz, logFreq);
          const x = margin.l + xn * innerW;
          const ynRaw = (c.spectrum_db[k] - yMin) / (yMax - yMin);
          const yn = Math.max(0, Math.min(1, ynRaw));
          const y = margin.t + (1 - yn) * innerH;
          points.push(`${points.length === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`);
        }
        const label = c.assigned_cyl != null ? `Cyl ${c.assigned_cyl} (P-${c.letter})` : `P-${c.letter}`;
        const yLegend = margin.t + 12 + idx * 14;
        return (
          <g key={c.template_slot}>
            <path d={points.join(' ')} stroke={color} strokeWidth={1.2} fill="none" />
            <line x1={W - margin.r + 8} x2={W - margin.r + 22}
                  y1={yLegend} y2={yLegend}
                  stroke={color} strokeWidth={2} />
            <text x={W - margin.r + 26} y={yLegend + 3} fontSize="10" fill="var(--fg)">
              {label}
            </text>
          </g>
        );
      })}
      {/* x-axis ticks */}
      {(logFreq ? [100, 1000, maxHz] : [0, maxHz / 2, maxHz]).map((f) => {
        const xn = freqAxisX(f, maxHz, logFreq);
        const x = margin.l + xn * innerW;
        const label = f >= 1000 ? `${(f / 1000).toFixed(0)} kHz` : `${f.toFixed(0)} Hz`;
        return (
          <text key={f} x={x} y={H - 6} fontSize="9" fill="var(--muted)" textAnchor="middle">
            {label}
          </text>
        );
      })}
    </svg>
  );
}

// ---- ICA-component-per-cylinder section ------------------------------
//
// Independent of the slot-binning ensemble. Lazy-loaded — clicking
// "Load ICA components" hits a separate endpoint that maps the K
// ICA components to slots by peak-time. Useful when slot-binning
// shows correlated cyls (which can happen when a cyl's energy
// leaks across slot boundaries) — ICA's source-separation is the
// principled tool for un-mixing those.

function IcaMappingSection({ recordingId }: { recordingId: number }) {
  const [data, setData] = useState<IcaMappingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<IcaMappingResponse>(
        `/api/admin/recordings/${recordingId}/cylinder-ica-mapping`,
      );
      setData(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>
          ICA components mapped per cylinder
          {data && data.cylinders.length > 0 && (
            <span className="muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
              {' '}({data.cylinders.reduce((s, c) => s + c.mapped_components.length, 0)} mapped,
              {' '}{data.unmapped.length} unmapped)
            </span>
          )}
        </h4>
        <button
          onClick={load}
          disabled={loading}
          className={data ? 'secondary' : undefined}
        >
          {loading ? 'Loading…' : data ? 'Reload' : 'Load ICA components'}
        </button>
      </div>
      {loading && (
        <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
          Loading… first load on a stale recording auto-recomputes ICA
          (~3-5 s); cached loads are near-instant.
        </div>
      )}
      {data?.recomputed && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid var(--ok)',
          color: 'var(--ok)',
          fontSize: '0.85rem',
          padding: '0.4rem 0.7rem',
          borderRadius: 4,
          marginTop: 6,
        }}>
          ✓ ICA was auto-recomputed for this view (the persisted analysis
          was missing or misaligned). The fresh result has been saved
          back to the recording — subsequent loads will be instant.
        </div>
      )}
      {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}
      <DescriptionBox
        title="What this view shows: GLOBAL ICA over the full engine cycle, mapped to slots"
        body={
          <>
            <p>
              ONE ICA decomposition is run over the whole engine cycle
              (all N slots stacked as one waveform per cycle). It returns
              K=10 statistically independent components, each one full
              cycle long. Each component is then assigned to whichever
              slot contains its peak-amplitude time — that's the cyl
              the component "belongs to".
            </p>
            <p>
              <strong>Expect</strong>: not a clean 1:1 mapping. Some cyls
              get 1–3 components (their firing source decomposed into
              sub-features by ICA — sharp impact + ringing tail commonly
              split). Some cyls may get 0 (their source got grouped with
              a louder neighbor or fell below the RMS gate). Non-cyl
              sources (alternator whine, balance-shaft hum, mic noise)
              that survive the gate land in arbitrary slots. This is a
              fundamental property of global ICA + heuristic mapping,
              not a bug — see the per-cyl ICA section below for the
              cleaner per-cyl decomposition.
            </p>
            <p>
              <strong>Synthetic V6 example</strong>: typical result —
              1–2 components per cyl from the firing bursts, occasional
              empty cyl when one cyl's signature was rolled into a
              neighbor's. Always 1+ unmapped components from the
              alternator (150 Hz) and balance-shaft hum (120 Hz).
              Useful diagnostic question this view answers: "what
              underlying engine sources exist, and which cyl is
              responsible for each?"
            </p>
          </>
        }
      />
      {!data && !loading && !error && (
        <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
          Click <strong>Load ICA components</strong> above to fetch.
        </div>
      )}
      {data?.error && (
        <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
          {data.error}
        </div>
      )}
      {data && !data.error && !data.ica_aligned && (
        <div className="error" style={{ fontSize: '0.85rem', marginTop: 6 }}>
          ⚠ Mapping unreliable: {data.alignment_warning}
        </div>
      )}
      {data && !data.error && (
        <>
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
            Each panel shows ICA components whose peak landed in that
            cyl's slot. Strongest (by RMS) drawn first; multiple components
            per cyl shown as colored overlays. X-axis covers the full
            engine cycle ({data.duration_per_cycle_ms.toFixed(1)} ms).
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
            gap: '0.5rem', marginTop: 8,
          }}>
            {data.cylinders.map((cy) => (
              <IcaCylPanel
                key={cy.template_slot}
                cyl={cy}
                cycleSamples={data.cycle_samples}
                durationMs={data.duration_per_cycle_ms}
                numCyl={data.num_cylinders}
              />
            ))}
          </div>
          {data.unmapped.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="muted" style={{ fontSize: '0.78rem', marginBottom: 4 }}>
                Unmapped components ({data.unmapped.length}) — likely
                non-cylinder sources (intake whine, mechanical noise, broadband)
                that don't have a slot-localized peak.
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                {data.unmapped.map((u) => (
                  <span key={u.component_index} style={{ marginRight: 12 }}>
                    comp #{u.component_index} (rms {u.rms.toFixed(4)}, peak@{u.peak_position_samples})
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IcaCylPanel({
  cyl, cycleSamples, durationMs, numCyl,
}: {
  cyl: IcaMappingResponse['cylinders'][number];
  cycleSamples: number;
  durationMs: number;
  numCyl: number;
}) {
  const W = 220, H = 120;
  const margin = { l: 26, r: 6, t: 6, b: 16 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;
  // Y-scale based on this cyl's components only (each cyl-panel
  // independent — different cyls have very different ICA scales).
  let peak = 0;
  for (const m of cyl.mapped_components) {
    for (const v of m.waveform) if (Math.abs(v) > peak) peak = Math.abs(v);
  }
  const yScale = peak > 0 ? peak * 1.1 : 1;
  const N = cyl.mapped_components[0]?.waveform.length ?? 0;
  const xOf = (i: number) => margin.l + (i / Math.max(1, N - 1)) * innerW;
  const yOf = (v: number) => margin.t + ((1 - (v / yScale + 1) / 2)) * innerH;

  // Slot boundary lines so user sees this cyl's "home slot" within
  // the full-cycle x-axis.
  const M = Math.floor(cycleSamples / numCyl);
  const slotStart = cyl.recording_slot * M;
  const slotEnd = (cyl.recording_slot + 1) * M;
  // map back to x — N samples = cycleSamples decimated
  const xSlotStart = margin.l + (slotStart / cycleSamples) * innerW;
  const xSlotEnd   = margin.l + (slotEnd   / cycleSamples) * innerW;

  const title = cyl.assigned_cyl != null ? `Cyl ${cyl.assigned_cyl}` : `P-${cyl.letter}`;
  const sub = cyl.mapped_components.length > 0
    ? `${cyl.mapped_components.length} comp${cyl.mapped_components.length === 1 ? '' : 's'}`
    : 'no mapping';

  return (
    <div style={{
      background: 'var(--panel-2)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '0.4rem 0.5rem',
    }}>
      <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '0.9rem' }}>{title}</strong>
        <span className="muted" style={{ fontSize: '0.7rem' }}>{sub}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height: H, display: 'block', marginTop: 2 }}>
        {/* Slot region highlight */}
        <rect x={xSlotStart} y={margin.t} width={Math.max(1, xSlotEnd - xSlotStart)}
              height={innerH} fill="var(--accent)" opacity={0.06} />
        {/* zero baseline */}
        <line
          x1={margin.l} x2={W - margin.r}
          y1={yOf(0)} y2={yOf(0)}
          stroke="var(--border)" strokeWidth={0.5} strokeDasharray="2 3"
        />
        {/* Y labels */}
        <text x={margin.l - 3} y={margin.t + 4} fontSize="8" fill="var(--muted)" textAnchor="end">
          {yScale.toFixed(2)}
        </text>
        <text x={margin.l - 3} y={margin.t + innerH - 1} fontSize="8" fill="var(--muted)" textAnchor="end">
          {(-yScale).toFixed(2)}
        </text>
        {/* Components — each in its own color */}
        {cyl.mapped_components.map((m, idx) => {
          const color = CYL_COLORS[idx % CYL_COLORS.length];
          const path = m.waveform
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`)
            .join(' ');
          return (
            <path key={m.component_index} d={path}
                  stroke={color} strokeWidth={1.2} fill="none" opacity={0.85} />
          );
        })}
        {/* x-axis labels */}
        <text x={margin.l} y={H - 3} fontSize="8" fill="var(--muted)">0</text>
        <text x={W - margin.r} y={H - 3} fontSize="8" fill="var(--muted)" textAnchor="end">
          {durationMs.toFixed(1)} ms
        </text>
        <text x={(xSlotStart + xSlotEnd) / 2} y={H - 3}
              fontSize="8" fill="var(--accent)" textAnchor="middle">
          slot
        </text>
      </svg>
      {cyl.mapped_components.length > 0 && (
        <div className="muted" style={{ fontSize: '0.7rem', marginTop: 2, lineHeight: 1.4 }}>
          {cyl.mapped_components.map((m, i) => {
            // mapping_strength = in-slot peak / max-other-slot peak.
            // >2 = clearly bound; 1.0–1.5 = ambiguous; <1 = peak
            // is actually stronger elsewhere (mapped here only because
            // the absolute argmax happened to land here).
            const ambig = m.mapping_strength < 1.3;
            return (
              <div key={m.component_index}>
                <span style={{ color: CYL_COLORS[i % CYL_COLORS.length] }}>■</span>{' '}
                c#{m.component_index} · var {(m.variance_fraction * 100).toFixed(1)}% · rms {m.rms.toFixed(3)} ·{' '}
                <span style={{ color: ambig ? '#f59e0b' : 'var(--ok)' }}>
                  strength {m.mapping_strength.toFixed(2)}× {ambig ? '(ambiguous)' : '(clean)'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Per-cylinder ICA decomposition section --------------------------
//
// Distinct from IcaMappingSection: that one runs ONE global ICA over
// the full cycle and maps components to slots heuristically. This one
// runs a SEPARATE ICA on each cyl's per-cycle ensemble — every cyl
// gets exactly K components describing sub-structure within that
// cyl's signal.

function PerCylIcaSection({ recordingId }: { recordingId: number }) {
  const [data, setData] = useState<PerCylIcaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [numComponents, setNumComponents] = useState(4);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<PerCylIcaResponse>(
        `/api/admin/recordings/${recordingId}/per-cyl-ica?num_components=${numComponents}`,
      );
      setData(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>
          Per-cylinder ICA decomposition
          {data && (
            <span className="muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
              {' '}({data.num_cylinders} cyls × {data.num_components} components,
              {' '}from {data.cycles_used} cycles)
            </span>
          )}
        </h4>
        <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
          }}>
            K (components/cyl)
            <select
              value={numComponents}
              onChange={(e) => setNumComponents(Number(e.target.value))}
              disabled={loading}
              style={{ width: 'auto', fontSize: '0.78rem', padding: '2px 4px' }}
            >
              {[2, 3, 4, 5, 6, 7, 8].map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </label>
          <button
            onClick={load}
            disabled={loading}
            className={data ? 'secondary' : undefined}
          >
            {loading ? 'Loading…' : data ? 'Reload' : 'Load per-cyl ICA'}
          </button>
        </div>
      </div>
      {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}
      <DescriptionBox
        title="What this view shows: PER-CYL ICA — one decomposition per cylinder"
        body={
          <>
            <p>
              For each cyl s, we extract that cyl's slot from every
              recorded engine cycle, build an N_cycles × M ensemble (one
              row per cycle), and run a SEPARATE ICA on it. Each cyl
              gets exactly K (default 4) components describing the
              sub-structure within THAT cyl's signal — not across cyls.
            </p>
            <p>
              <strong>Expect</strong>: every cyl always gets exactly K
              components, ranked by descending variance. Component 1 is
              usually the dominant source for that cyl (the firing
              event); components 2–K capture lower-variance modes
              (mechanical resonance, valve-train noise, cycle-to-cycle
              variability, residual). Comparing cyl-N's component-1 to
              cyl-M's component-1 answers "do all cyls have similar
              underlying mechanical structure?". A cyl whose component-1
              is unlike its peers' is a candidate for asymmetry / wear.
            </p>
            <p>
              <strong>Synthetic V6 example</strong>: each cyl's
              component-1 should look like its synthesized burst
              (different fundamental + harmonic count per cyl, so the
              shapes should clearly differ across cyls). Components
              2–K should be small noise / hum residuals that look
              roughly similar across cyls (same ambient sources).
              The variance ratio between component 1 and component 2
              roughly indicates how dominant the firing event is vs
              ambient noise for that cyl.
            </p>
            <p>
              <strong>Tradeoff vs the global ICA above</strong>: this
              view discards cross-cyl independence (the very thing
              global ICA exploits). Better for "what's INSIDE each
              cyl?"; the global view is better for "what underlying
              engine sources exist and which cyl owns each?". They
              answer different questions.
            </p>
          </>
        }
      />
      {!data && !loading && !error && (
        <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
          Click <strong>Load per-cyl ICA</strong> above to fetch.
        </div>
      )}
      {data && (
        <>
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
            Slot duration: {data.duration_per_slot_ms.toFixed(2)} ms.
            X-axis = within-slot time. Components colored by index
            (component 1 = strongest variance contributor).
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
            gap: '0.5rem', marginTop: 8,
          }}>
            {data.cylinders.map((cy) => (
              <PerCylIcaPanel
                key={cy.template_slot}
                cyl={cy}
                durationMs={data.duration_per_slot_ms}
                K={data.num_components}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PerCylIcaPanel({
  cyl, durationMs, K,
}: {
  cyl: PerCylIcaResponse['cylinders'][number];
  durationMs: number;
  K: number;
}) {
  const W = 220, H = 140;
  const margin = { l: 26, r: 6, t: 6, b: 16 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;
  const title = cyl.assigned_cyl != null ? `Cyl ${cyl.assigned_cyl}` : `P-${cyl.letter}`;
  const sub = `slot #${cyl.recording_slot + 1}`;

  if (cyl.error) {
    return (
      <div style={{
        background: 'var(--panel-2)', border: '1px solid var(--border)',
        borderRadius: 6, padding: '0.4rem 0.5rem',
      }}>
        <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
          <strong style={{ fontSize: '0.9rem' }}>{title}</strong>
          <span className="muted" style={{ fontSize: '0.7rem' }}>{sub}</span>
        </div>
        <div className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
          {cyl.error}
        </div>
      </div>
    );
  }

  // Independent y-scale per cyl — ICA components are arbitrarily
  // scaled within each cyl, so cross-panel comparison isn't
  // meaningful for absolute amplitude.
  let peak = 0;
  for (const c of cyl.components) {
    for (const v of c) if (Math.abs(v) > peak) peak = Math.abs(v);
  }
  const yScale = peak > 0 ? peak * 1.1 : 1;
  const N = cyl.components[0]?.length ?? 0;
  const xOf = (i: number) => margin.l + (i / Math.max(1, N - 1)) * innerW;
  const yOf = (v: number) => margin.t + ((1 - (v / yScale + 1) / 2)) * innerH;

  return (
    <div style={{
      background: 'var(--panel-2)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '0.4rem 0.5rem',
    }}>
      <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '0.9rem' }}>{title}</strong>
        <span className="muted" style={{ fontSize: '0.7rem' }}>{sub}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height: H, display: 'block', marginTop: 2 }}>
        {/* zero baseline */}
        <line
          x1={margin.l} x2={W - margin.r}
          y1={yOf(0)} y2={yOf(0)}
          stroke="var(--border)" strokeWidth={0.5} strokeDasharray="2 3"
        />
        {/* y-axis labels */}
        <text x={margin.l - 3} y={margin.t + 4} fontSize="8" fill="var(--muted)" textAnchor="end">
          {yScale.toFixed(2)}
        </text>
        <text x={margin.l - 3} y={margin.t + innerH - 1} fontSize="8" fill="var(--muted)" textAnchor="end">
          {(-yScale).toFixed(2)}
        </text>
        {/* component traces */}
        {cyl.components.map((comp, idx) => {
          const color = CYL_COLORS[idx % CYL_COLORS.length];
          const path = comp
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`)
            .join(' ');
          return (
            <path key={idx} d={path}
                  stroke={color} strokeWidth={1.2} fill="none" opacity={0.85} />
          );
        })}
        {/* x-axis labels */}
        <text x={margin.l} y={H - 3} fontSize="8" fill="var(--muted)">0</text>
        <text x={W - margin.r} y={H - 3} fontSize="8" fill="var(--muted)" textAnchor="end">
          {durationMs.toFixed(1)} ms
        </text>
      </svg>
      {cyl.compvars.length > 0 && (
        <div className="muted" style={{ fontSize: '0.7rem', marginTop: 2,
                                        display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {cyl.compvars.slice(0, K).map((v, i) => {
            const frac = cyl.variance_fractions?.[i] ?? 0;
            return (
              <span key={i}>
                <span style={{ color: CYL_COLORS[i % CYL_COLORS.length] }}>■</span>{' '}
                c{i + 1} {(frac * 100).toFixed(1)}%
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Reusable description info-box -----------------------------------

function DescriptionBox({
  title, body,
}: { title: string; body: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      borderLeft: '3px solid var(--accent)',
      borderRadius: 4,
      padding: expanded ? '0.5rem 0.7rem' : '0.3rem 0.7rem',
      marginTop: 8, marginBottom: 6,
      fontSize: '0.85rem',
      lineHeight: 1.45,
    }}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <strong>{expanded ? '▾' : '▸'} {title}</strong>
        <span className="muted" style={{ fontSize: '0.75rem' }}>
          {expanded ? 'click to collapse' : 'click for details'}
        </span>
      </div>
      {expanded && (
        <div style={{ marginTop: 6 }}>
          {body}
        </div>
      )}
    </div>
  );
}

